import { router } from '@inertiajs/react'
import { useState } from 'react'
import { ArrowLeftIcon, MailIcon, PhoneIcon, SparklesIcon } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { DeleteConfirmationDialog } from '@/components/delete-confirmation-dialog'

interface DriverLead {
  id: number
  name: string
  phone: string | null
  email: string | null
  source: string
  source_id: string | null
  instagram_username: string | null
  messenger_username: string | null
  driver_type: string
  cdl_status: string
  years_experience: number
  work_authorization: string
  currently_working: string
  status: string
  notes: string | null
  created_at: string
}

interface DriverLeadsShowProps {
  driverLead: DriverLead
}

export default function DriverLeadsShow({ driverLead }: DriverLeadsShowProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const handleDelete = () => {
    router.delete(`/driver_leads/${driverLead.id}`)
  }

  const formatStatus = (status: string) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
  }

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'hired':
        return 'default'
      case 'rejected':
        return 'destructive'
      default:
        return 'secondary'
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
          {/* Header */}
          <div className="px-4 lg:px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => router.visit('/driver_leads')}
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                </Button>
                <div>
                  <h1 className="text-3xl font-bold tracking-tight">{driverLead.name}</h1>
                  <p className="text-muted-foreground mt-1">
                    Driver Lead Details
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => router.post(`/driver_leads/${driverLead.id}/analyze_conversation`)}>
                  <SparklesIcon className="mr-2 size-4" />
                  Analyze Conversation
                </Button>
                <Button variant="outline" onClick={() => router.visit(`/driver_leads/${driverLead.id}/edit`)}>
                  Edit
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDeleteDialogOpen(true)}
                  className="text-destructive hover:text-destructive"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 lg:px-6">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Contact Information */}
              <Card>
                <CardHeader>
                  <CardTitle>Contact Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Name</div>
                    <div className="text-lg">{driverLead.name}</div>
                  </div>

                  {/* Social Media ID */}
                  {(() => {
                    let socialMediaId = null
                    if (driverLead.source === 'Instagram' && driverLead.instagram_username) {
                      socialMediaId = `@${driverLead.instagram_username}`
                    } else if (driverLead.source === 'Facebook Messenger' && driverLead.messenger_username) {
                      socialMediaId = `@${driverLead.messenger_username}`
                    } else if (['Facebook', 'LinkedIn', 'Indeed'].includes(driverLead.source) && driverLead.source_id) {
                      socialMediaId = `@${driverLead.source_id}`
                    }

                    return socialMediaId ? (
                      <div>
                        <div className="text-sm font-medium text-muted-foreground">Social Media ID</div>
                        <div>{socialMediaId}</div>
                      </div>
                    ) : null
                  })()}

                  {/* Phone */}
                  {driverLead.phone && (
                    <div className="flex items-center gap-2">
                      <PhoneIcon className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="text-sm font-medium text-muted-foreground">Phone</div>
                        <div>{driverLead.phone.replace(/[^0-9]/g, '')}</div>
                      </div>
                    </div>
                  )}

                  {driverLead.email && (
                    <div className="flex items-center gap-2">
                      <MailIcon className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="text-sm font-medium text-muted-foreground">Email</div>
                        <div>{driverLead.email}</div>
                      </div>
                    </div>
                  )}

                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Source</div>
                    <Badge variant="outline" className="mt-1">{driverLead.source}</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Driver Qualifications */}
              <Card>
                <CardHeader>
                  <CardTitle>Driver Qualifications</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Driver Type</div>
                    <div className="text-lg">{driverLead.driver_type || 'Not specified'}</div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-muted-foreground">CDL Status</div>
                    <Badge variant="secondary" className="mt-1">{driverLead.cdl_status || 'Not specified'}</Badge>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Years of Experience</div>
                    <div className="text-lg">{driverLead.years_experience ?? 0} years</div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Work Authorization</div>
                    <div>{driverLead.work_authorization || 'Not specified'}</div>
                  </div>

                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Currently Working</div>
                    <div>{driverLead.currently_working || 'Not specified'}</div>
                  </div>
                </CardContent>
              </Card>

              {/* Recruitment Status */}
              <Card>
                <CardHeader>
                  <CardTitle>Recruitment Status</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground">Current Status</div>
                    <Badge variant={getStatusBadgeVariant(driverLead.status)} className="mt-1">
                      {formatStatus(driverLead.status)}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              {driverLead.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle>Notes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm whitespace-pre-wrap">{driverLead.notes}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDelete}
        title="Delete Driver Lead"
        description={`Are you sure you want to delete ${driverLead.name}? This will permanently remove the driver lead and all associated data.`}
      />
    </div>
  )
}
